import Bookmark from "./Bookmark";
import Note from "./Note";
import { cfiPairFromCfiRange } from "../CfiUtils";

const URI_PATH_BOOKMARKS = "/bookmarks";
const URI_PATH_NOTES = "/highlights";

export default class AnnotationApiClient {
  /**
   * Retrieves bookmarks
   * @param {number} bookId
   * @returns {Promise<Bookmark[]>} Promise that resolves to an array of Bookmarks
   */
  async getBookmarks(bookId) {
    const qsParams = new URLSearchParams([["bookshareId", bookId]]);
    const url = `${URI_PATH_BOOKMARKS}?${qsParams.toString()}`;

    // This one we want quickly, and we have a local fallback, so no retry.
    const response = await this.fetchWithRetry(url, {
      method: "GET",
      retries: 0,
    });
    const parsedJson = await response.json();

    /** @type {Bookmark[]} */
    const result = [];
    parsedJson.forEach((j) => {
      const b = Bookmark.fromJson(j);
      // Server-retrieved bookmarks are always synced
      b.synced = true;
      result.push(b);
    });
    return result;
  }

  /**
   * Saves a single bookmark
   * @param {Bookmark} bookmark
   * @returns {Promise<Response>}
   */
  saveBookmark(bookmark) {
    const formParams = new URLSearchParams([
      ["bookshareId", bookmark.bookId],
      ["location", bookmark.location],
      ["text", bookmark.text],
    ]);
    const url = URI_PATH_BOOKMARKS;
    const locatorJson = JSON.stringify(bookmark.locator, [
      "position",
      "progression",
      "totalProgression",
    ]);
    if (locatorJson !== "{}") {
      formParams.set("locator", locatorJson);
    }

    return this.fetchWithRetry(url, {
      method: "POST",
      body: formParams.toString(),
    });
  }

  /**
   * Deletes a single bookmark
   * @param {Bookmark} bookmark
   * @returns {Promise<Response>}
   */
  deleteBookmark(bookmark) {
    const qsParams = new URLSearchParams([
      ["bookshareId", bookmark.bookId],
      ["location", bookmark.location],
    ]);
    const url = `${URI_PATH_BOOKMARKS}?${qsParams.toString()}`;

    return this.fetchWithRetry(url, { method: "DELETE" });
  }

  /**
   * Retrieves notes
   * @param {number} bookId
   * @returns {Promise<Note[]>} Promise that resolves to an array of Notes
   */
  async getNotes(bookId) {
    const qsParams = new URLSearchParams([["bookshareId", bookId]]);
    const url = `${URI_PATH_NOTES}?${qsParams.toString()}`;

    // This one we want quickly, and we have a local fallback, so no retry.
    const response = await this.fetchWithRetry(url, {
      method: "GET",
      retries: 0,
    });
    const parsedJson = await response.json();

    /** @type {Note[]} */
    const result = [];
    parsedJson.forEach((j) => {
      const n = Note.fromJson(j);
      // Server-retrieved notes are always synced
      n.synced = true;
      result.push(n);
    });
    return result;
  }

  /**
   * Saves a single note
   * @param {Note} note
   * @returns {Promise<Response>}
   */
  saveNote(note) {
    const cfiPair = cfiPairFromCfiRange(note.location);

    const formParams = new URLSearchParams([
      ["bookshareId", note.bookId],
      ["startLocation", cfiPair.start],
      ["endLocation", cfiPair.end],
      ["text", note.text || ""],
      ["color", note.color],
      ["note", note.note || ""],
    ]);
    const url = URI_PATH_NOTES;
    const locatorJson = JSON.stringify(note.locator, [
      "position",
      "progression",
      "totalProgression",
    ]);
    if (locatorJson !== "{}") {
      formParams.set("locator", locatorJson);
    }

    return this.fetchWithRetry(url, {
      method: "POST",
      body: formParams.toString(),
    });
  }

  /**
   * Deletes a single note
   * @param {Note} note
   * @returns {Promise<Response>}
   */
  deleteNote(note) {
    const cfiPair = cfiPairFromCfiRange(note.location);
    const qsParams = new URLSearchParams([
      ["bookshareId", note.bookId],
      ["startLocation", cfiPair.start],
      ["endLocation", cfiPair.end],
    ]);
    const url = `${URI_PATH_NOTES}?${qsParams.toString()}`;

    return this.fetchWithRetry(url, { method: "DELETE" });
  }

  ///TODO: Edit does not seem to work on the API - fix it
  /**
   * Edit a note
   * @param {Note} note
   * @returns {Promise<Response>}
   */
  editNote(note) {
    const cfiPair = cfiPairFromCfiRange(note.location);
    const qsParams = new URLSearchParams([
      ["bookshareId", note.bookId],
      ["startLocation", cfiPair.start],
      ["endLocation", cfiPair.end],
      ["note", note.note || ""],
      ["color", note.color],
    ]);
    const url = `${URI_PATH_NOTES}?${qsParams.toString()}`;

    return this.fetchWithRetry(url, { method: "PUT" });
  }

  /**
   * Works the same as fetch but adds support for retries, controlled by 3 new
   * init options:
   * - retries: max number of retries
   * - retryAttempts: number of retries already attempted
   * - delay: delay in milliseconds between retries
   *
   * Useful even if you don't want to use the retry mechanism, because it
   * automatically rejects any non-200 response.
   *
   * @param {RequestInfo | URL} input URL
   * @param {*} init
   * @returns {Promise<Response>}
   */
  fetchWithRetry(input, init = {}) {
    const { retryAttempts = 0, retries = 2, delay = 3000, ...options } = init;
    const response = fetch(input, options)
      .then((response) => {
        if (response.status === 200) {
          // Resolves with success
          return response;
        } else {
          // Throws an error to trigger the catch
          throw new Error(
            `Response was HTTP ${response.status} ${response.statusText}: ${response.body}`,
          );
        }
      })
      .catch(() => {
        // May be triggered by either a fetch rejection or a thrown error due to non-200 HTTP responses
        if (retryAttempts < retries) {
          const retryOptions = {
            retries: retries,
            retryAttempts: retryAttempts + 1,
            delay: delay,
          };

          Object.assign(retryOptions, options);

          // Use setTimeout to queue up another attempt
          return new Promise((resolve) => {
            setTimeout(
              () => resolve(this.fetchWithRetry(input, retryOptions)),
              delay,
            );
          });
        } else {
          throw new Error(
            `${options.method || "GET"} request to ${input} failed after ${retryAttempts} retries.`,
          );
        }
      });
    return response;
  }
}
